// Copyright (c) Docugami, Inc. All rights reserved.

import { Checkbox, ContextualMenu, Dropdown, IconButton, List, Modal, Stack, TooltipHost } from "@fluentui/react";
import { ChunkItemSlot } from "components/cards/ChunkItemSlot";
import { SlotItemType } from "components/cards/ItemSlot";
import { MetricsStatusBar } from "components/doc_hosting/presentation/MetricsStatusBar";
import { humanBlue, machineGreen, themePink, themeYellow } from "components/shell/presentation/AppStyles";
import { useChunkSelection } from "hooks/chunkSelection";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { debounce } from "lodash";
import { TokenRange } from "networking/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SdvInterface } from "sdv/SdvInterface";
import { AppDispatch, AppState } from "store";
import { selectMappedTokens } from "store/dgml/dgmlSelectors";
import { setSelectionMode } from "store/documents/actions";
import { CanvasSelectionMode } from "store/documents/types";
import { ExpandRepeatsRequest, useExpandMutation } from "store/repeatsSlice";
import { createOperationId, SetEntryBindingsInput, useSetEntryBindingsMutation } from "store/reports/reportsSlice";
import { selectOrderedReportEntries, selectReportContext, selectReportId } from "store/reports/selectors";
import { selectCurrentChunkInstance } from "store/sdvSelectors";
import { removePreviewChunkSet, setPreviewChunks } from "store/sdvSlice";
import styled from "styled-components";
import { SpanId, SpanIdUtils } from "utils/SpanIdUtils";
import { TokenRangeUtils } from "utils/TokenRangeUtils";

const RootContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 500px;
    height: 500px;
    gap: 5px;
    padding: 5px;
    box-sizing: border-box;
`

const Header = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
    :hover {
        cursor: move;
    }
`

const Title = styled.span`
    flex: 1;
    font-weight: bold;
    font-size: 16px;
`

const ListHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
`

const ListCounts = styled.div`
    font-weight: bold;
    font-size: 10px;
    margin-left: auto;
    display: flex;
    gap: 10px;
    display: flex;
    align-items: center;
`

const ListTitle = styled.span`
    font-weight: bold;
    font-size: 12px;
`

const ListsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 5px;
    height: 100%;
    width: 100%;
    overflow: hidden;
`

const ListContainer = styled.div<{ flex?: number }>`
    flex: ${({ flex = 1 }) => flex};
    border: 1px solid black;
    padding: 5px;
    box-sizing: border-box;
    overflow: hidden;
    border-radius: 5px;
`

export interface RepeatsEditorProps {
    clickTarget?: HTMLElement;
    isOpen?: boolean;
    onClose?: () => void;
    sdvInterface?: SdvInterface;
}

export const RepeatsEditorWindow = (props: RepeatsEditorProps): JSX.Element => {
    const { clickTarget, isOpen } = props;

    const docsetId = useAppSelector(state => state.sdv.document?.docsetId ?? '');
    const fileId = useAppSelector(state => state.sdv.document?.fileId ?? '');

    const mappedTokens = useAppSelector(state => selectMappedTokens(state, docsetId, fileId));
    const targetSpanId = useAppSelector(selectCurrentChunkInstance)?.spanId;

    const [positiveSpanIds, setPositiveSpanIds] = useState<RepeatInstance[]>([]);
    const [negativeSpanIds, setNegativeSpanIds] = useState<RepeatInstance[]>([]);
    const [anchors, setAnchors] = useState<RepeatInstance[]>([]);
    const [statusMetrics, setStatusMetrics] = useState<[number, number, number]>([0, 0, 0])
    const dispatch = useAppDispatch();

    const { clearSelection } = useChunkSelection();

    const getTokenRange = useCallback((spanId: string) => SpanIdUtils.getTokenRange(mappedTokens, SpanIdUtils.parse(spanId))!, [mappedTokens]);
    const [triggerExpand, { data: rawExpandResults, reset: resetExpandTrigger, isLoading }] = useExpandMutation();
    const expandResults = useMemo(() => rawExpandResults?.results.map<RepeatInstance>(x => ({ spanId: x, tokenRange: getTokenRange(x.value), isHuman: false })) ?? [], [rawExpandResults, getTokenRange]);
    const debounceExpand = useMemo(() => debounce(triggerExpand, 1000, { leading: true }), [triggerExpand]);

    const reset = useCallback((keepAnchors: boolean = false) => {
        setPositiveSpanIds([]);
        setNegativeSpanIds([]);
        if (!keepAnchors) {
            setAnchors([]);
        }
        resetExpandTrigger();
        setStatusMetrics([0, 0, 0]);
        clearSelection();
    }, [resetExpandTrigger, clearSelection]);

    useEffect(() => {
        reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docsetId, fileId, props.isOpen])

    useEffect(() => {
        if (isOpen) {
            if (positiveSpanIds.length > 0 || negativeSpanIds.length > 0) {
                const request: ExpandRepeatsRequest = {
                    docsetId: docsetId,
                    fileId: fileId,
                    positiveSpans: positiveSpanIds.map(x => x.spanId.value),
                    negativeSpans: negativeSpanIds.map(x => x.spanId.value),
                    anchorSpans: anchors.map(x => x.spanId.value)
                }
                debounceExpand(request);
            }
            else {
                resetExpandTrigger();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveSpanIds, negativeSpanIds, anchors, debounceExpand, docsetId, fileId, isOpen]);

    useEffect(() => {
        return () => {
            dispatch(removePreviewChunkSet('repeatsDemo-user'));
            dispatch(removePreviewChunkSet('repeatsDemo-machine'));
            dispatch(removePreviewChunkSet('repeatsDemo-negative'));
            dispatch(removePreviewChunkSet('repeatsDemo-anchors'));
        }
    }, [dispatch])

    useEffect(() => {
        clearSelection();
        props.sdvInterface?.resize?.end(false);
    }, [clearSelection, props.sdvInterface, positiveSpanIds, negativeSpanIds])

    function removePositive(spanId?: string) {
        if (!spanId) {
            return;
        }

        if (positiveSpanIds.some(x => x.spanId.value === spanId)) {
            setPositiveSpanIds(prev => prev.filter(x => x.spanId.value !== spanId));
        }
    }

    function removeNegative(spanId?: string) {
        if (!spanId) {
            return;
        }

        if (negativeSpanIds.some(x => x.spanId.value === spanId)) {
            setNegativeSpanIds(prev => prev.filter(x => x.spanId.value !== spanId));
        }
    }

    const addPositive = useCallback((spanId?: string) => {
        if (!spanId) {
            return;
        }

        if (positiveSpanIds.some(x => x.spanId.value === spanId)) {
            setPositiveSpanIds(prev => prev.filter(x => x.spanId.value !== spanId));
            return;
        }

        if (anchors.some(x => x.spanId.value === spanId)) {
            return;
        }

        const tokenRange = getTokenRange(spanId);
        const others = expandResults
            .filter(x => x.tokenRange.toTok <= tokenRange.fromTok && !positiveSpanIds.some(y => y.tokenRange.fromTok === x.tokenRange.fromTok))

        const instance: RepeatInstance = {
            spanId: SpanIdUtils.parse(spanId),
            tokenRange: tokenRange,
            isHuman: true
        }

        setPositiveSpanIds(prev => [...prev, instance, ...others]);
        setNegativeSpanIds(prev => prev.some(x => x.tokenRange.fromTok === tokenRange.fromTok) ? prev.filter(x => x.tokenRange.fromTok !== tokenRange.fromTok) : prev);
    }, [positiveSpanIds, anchors, getTokenRange, expandResults]);

    const addNegative = useCallback((spanId?: string) => {
        if (!spanId) {
            return;
        }

        if (negativeSpanIds.some(x => x.spanId.value === spanId)) {
            setNegativeSpanIds(prev => prev.filter(x => x.spanId.value !== spanId));
            return;
        }

        const tokenRange = getTokenRange(spanId);
        const extraPositiveResults = expandResults
            .filter(x => x.tokenRange.toTok <= tokenRange.fromTok && !positiveSpanIds.some(y => y.tokenRange.fromTok === x.tokenRange.fromTok))

        const instance: RepeatInstance = {
            spanId: SpanIdUtils.parse(spanId),
            tokenRange: tokenRange,
            isHuman: true
        }

        setNegativeSpanIds(prev => [...prev, instance]);
        setPositiveSpanIds(prev => prev.some(x => x.tokenRange.fromTok === tokenRange.fromTok) || !!extraPositiveResults.length ?
            [...prev.filter(x => x.tokenRange.fromTok !== tokenRange.fromTok), ...extraPositiveResults] :
            prev);
    }, [positiveSpanIds, negativeSpanIds, getTokenRange, expandResults]);

    useEffect(() => {
        if (!clickTarget || !isOpen) {
            return;
        }

        const callback = (ev: MouseEvent) => {
            if (!!targetSpanId && ev.shiftKey) {
                if (ev.altKey || ev.ctrlKey) {
                    // Remove
                    addNegative(targetSpanId);
                }
                else {
                    // Add
                    addPositive(targetSpanId);
                }
            }
        }

        const clickHandler = (ev: MouseEvent) => callback(ev);
        clickTarget?.addEventListener("click", clickHandler);

        return () => {
            clickTarget?.removeEventListener("click", clickHandler);
        }
    }, [clickTarget, targetSpanId, addPositive, addNegative, isOpen])

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const callback = (ev: KeyboardEvent) => {
            if (!!targetSpanId) {
                if (ev.key === 'f') {
                    addPositive(targetSpanId);
                }
                else if (ev.key === 'r') {
                    addNegative(targetSpanId);
                }
            }
        }

        const clickHandler = (ev: KeyboardEvent) => callback(ev);
        document.addEventListener("keypress", clickHandler);

        return () => {
            document.removeEventListener("keypress", clickHandler);
        }
    }, [targetSpanId, addPositive, addNegative, isOpen])

    const orderedPositive = useMemo(() => dispatch(spanIdReadingOrderComparerThunk(positiveSpanIds, true)), [positiveSpanIds, dispatch]);
    const orderedNegative = useMemo(() => dispatch(spanIdReadingOrderComparerThunk(negativeSpanIds, true)), [negativeSpanIds, dispatch]);
    const allResults = useMemo(() => dispatch(spanIdReadingOrderComparerThunk([...positiveSpanIds, ...expandResults], true)), [positiveSpanIds, expandResults, dispatch])

    useEffect(() => {
        if (props.isOpen) {
            clearSelection();
            dispatch(setSelectionMode(CanvasSelectionMode.Highlight));
        }
    }, [props.isOpen, dispatch, clearSelection])

    useEffect(() => {
        dispatch(setPreviewChunks({
            id: 'repeatsDemo-user',
            chunks: {
                spanIds: allResults.filter(x => x.isHuman).map(x => x.spanId.value),
                color: humanBlue
            }
        }))

        dispatch(setPreviewChunks({
            id: 'repeatsDemo-machine',
            chunks: {
                spanIds: allResults.filter(x => !x.isHuman).map(x => x.spanId.value),
                color: machineGreen
            }
        }))
    }, [allResults, dispatch]);

    useEffect(() => {
        dispatch(setPreviewChunks({
            id: 'repeatsDemo-negative',
            chunks: {
                spanIds: negativeSpanIds.map(x => x.spanId.value),
                color: themeYellow
            }
        }))
    }, [dispatch, negativeSpanIds]);

    useEffect(() => {
        dispatch(setPreviewChunks({
            id: 'repeatsDemo-anchors',
            chunks: {
                spanIds: anchors.map(x => x.spanId.value),
                color: themePink
            }
        }))
    }, [dispatch, anchors]);

    useEffect(() => {
        if (!isLoading) {
            const total = allResults.length;
            if (total === 0) {
                setStatusMetrics([0, 0, 0]);
            }
            else {
                const humanCount = allResults.filter(x => x.isHuman).length;
                const humanPercentage = Math.ceil(humanCount / total * 100);
                setStatusMetrics([total, humanPercentage, 100 - humanPercentage]);
            }
        }
    }, [allResults, isLoading])

    const [, humanPercentage, machinePercentage] = statusMetrics;

    return (
        <Modal
            isOpen={props.isOpen}
            isBlocking={false}
            isDarkOverlay={false}
            isModeless={true}
            isClickableOutsideFocusTrap={true}
            forceFocusInsideTrap={false}
            dragOptions={{
                moveMenuItemText: 'Move',
                closeMenuItemText: 'Close',
                menu: ContextualMenu,
                dragHandleSelector: ".dragTarget"
            }}>
            <RootContainer>
                <Header className="dragTarget">
                    <Title>
                        Repeats Editor
                    </Title>
                    <TooltipHost content={"Clear inputs and preserve anchors (if set)"}>
                        <IconButton iconProps={{ iconName: "RemoveOccurrence" }} onClick={() => reset(true)} />
                    </TooltipHost>
                    <TooltipHost content={"Set current results as anchors"}>
                        <IconButton iconProps={{ iconName: "AnchorLock" }} onClick={() => {
                            const results = [...allResults]
                            reset();
                            setAnchors(results)
                        }} />
                    </TooltipHost>
                    <TooltipHost content={"Clear all"}>
                        <IconButton iconProps={{ iconName: "Delete" }} onClick={() => reset()} />
                    </TooltipHost>
                    <TooltipHost content={"Close"}>
                        <IconButton iconProps={{ iconName: "ChromeClose" }} onClick={() => {
                            props.onClose?.()
                            dispatch(setSelectionMode(CanvasSelectionMode.Select));
                        }} />
                    </TooltipHost>
                </Header>
                <MetricsStatusBar items={[
                    { name: 'Human', color: humanBlue, percentage: humanPercentage },
                    { name: 'Machine', color: machineGreen, percentage: machinePercentage }
                ]} />
                <ListsContainer>
                    <Stack tokens={{ childrenGap: 5 }} styles={{ root: { flex: 1 } }}>
                        <ListContainer flex={2}>
                            <RepeatsList title="Inputs" repeats={orderedPositive} onAdd={() => addPositive(targetSpanId)} onRemove={() => removePositive(targetSpanId)} />
                        </ListContainer>
                        <ListContainer flex={1}>
                            <RepeatsList title="Rejected" repeats={orderedNegative} onAdd={() => addNegative(targetSpanId)} onRemove={() => removeNegative(targetSpanId)} />
                        </ListContainer>
                    </Stack>
                    <ListContainer flex={1}>
                        <RepeatsList title="Results" repeats={allResults} />
                    </ListContainer>
                </ListsContainer>
                <ReportEntrySelector spanIds={allResults?.map(x => x.spanId.value)} />
            </RootContainer>
        </Modal>
    );
}

interface RepeatInstance {
    spanId: SpanId;
    tokenRange: TokenRange;
    isHuman: boolean;
}

interface RepeatsListProps {
    title?: string;
    repeats: RepeatInstance[];
    onAdd?: () => void
    onRemove?: () => void

}

const RepeatsList = (props: RepeatsListProps): JSX.Element => {
    const { repeats } = props;
    const items = useMemo<RepeatItemProps[]>(() => repeats.map<RepeatItemProps>(x => ({ repeat: x })), [repeats]);

    const onRenderItem = (item?: RepeatItemProps, index?: number, isScrolling?: boolean): JSX.Element | null =>
        !!item ? <RepeatItem {...item} /> : null

    return (
        <Stack styles={{ root: { height: '100%', overflow: 'hidden' } }}>
            <ListHeader>
                {!!props.title && <ListTitle>{props.title}</ListTitle>}
                <ListCounts>
                    <div>
                        H: {repeats.filter(x => x.isHuman).length}
                    </div>
                    <div>
                        M: {repeats.filter(x => !x.isHuman).length}
                    </div>
                    <div>
                        Total: {repeats.length}
                    </div>
                    {!!props.onAdd && <IconButton
                        styles={{ root: { maxWidth: 15, maxHeight: 15 } }}
                        iconProps={{ iconName: "Add", styles: { root: { fontSize: 10 } } }}
                        onClick={props.onAdd}
                        title="Add instance" />}
                    {!!props.onRemove && <IconButton
                        styles={{ root: { maxWidth: 15, maxHeight: 15 } }}
                        iconProps={{ iconName: "Remove", styles: { root: { fontSize: 10 } } }}
                        onClick={props.onRemove}
                        title="Remove instance" />}
                </ListCounts>
            </ListHeader>
            <div style={{ overflowY: 'auto', flex: 1 }}>
                <List
                    onRenderCell={onRenderItem}
                    items={items}
                    onShouldVirtualize={() => false}
                />
            </div>
        </Stack>
    );
}

const RepeatItemRoot = styled.div`
    width: 100%;
    flex-direction: row;
    display: flex;
    align-items: stretch; // Ensure children fill the height
`

const RepeatItemIndicator = styled.div<{ color: string }>`
    width: 5px;
    margin: 5px 0px;
    background-color: ${({ color }) => color};
    flex-shrink: 0; // Prevent shrinking
`

interface RepeatItemProps {
    repeat: RepeatInstance;
    onConfirm?: () => void;
}

const RepeatItem = (props: RepeatItemProps): JSX.Element => {
    const { selectChunk } = useChunkSelection();
    return (
        <RepeatItemRoot>
            <RepeatItemIndicator color={props.repeat.isHuman ? humanBlue : machineGreen} />
            <ChunkItemSlot item={
                {
                    id: props.repeat.spanId.value,
                    type: SlotItemType.Chunk,
                    spanId: props.repeat.spanId.value,
                }
            }
                onClick={() => selectChunk(props.repeat.spanId.value)} />
        </RepeatItemRoot>
    )
};

export const spanIdReadingOrderComparerThunk = (items: RepeatInstance[], dropDuplicates: boolean) => (dispatch: AppDispatch, getState: () => AppState) => {
    if (items.length === 0) {
        return items;
    }

    const state = getState();
    const document = state.sdv.document;
    if (!!document) {
        items.sort((x, y) => {
            return TokenRangeUtils.readingOrderComparer(x.tokenRange, y.tokenRange)
        })

        if (dropDuplicates) {
            const result: RepeatInstance[] = [];
            let current: RepeatInstance = items[0];
            result.push(current);
            for (let i = 1; i < items.length; i++) {
                if (items[i].tokenRange.fromTok !== current.tokenRange.fromTok) {
                    result.push(items[i]);
                    current = items[i];
                }
            }

            return result;
        }
        else {
            return items;
        }
    }

    return items;
}

interface ReportEntrySelectorProps {
    spanIds?: string[];
}

const ReportEntrySelector = (props: ReportEntrySelectorProps): JSX.Element => {
    const { spanIds } = props;
    const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
    const [replaceExisting, setReplaceExisting] = useState(false);

    const reportId = useAppSelector(selectReportId);
    const context = useAppSelector(selectReportContext);
    const folderId = useAppSelector(state => state.sdv.document?.folderId ?? '');
    const entries = useAppSelector(selectOrderedReportEntries);
    const [executeSetEntryBindings, { isLoading: isSetEntryBindingsActive }] = useSetEntryBindingsMutation();

    const canConfirm = !!folderId && !!context && !!reportId && !!spanIds?.length && !!selectedId && !isSetEntryBindingsActive;
    async function confirm() {
        if (!canConfirm) {
            return;
        }

        const entry = entries.find(x => x.id === selectedId);
        if (!entry) {
            return;
        }

        const request: SetEntryBindingsInput = {
            reportId: reportId,
            context: context!,
            request: {
                entryId: entry.id,
                folderId: folderId,
                spanIds: spanIds,
                replace: replaceExisting,
                operationId: createOperationId()
            }
        };

        await executeSetEntryBindings(request);
    }

    return (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
            <Dropdown
                placeholder="Select an entry..."
                styles={{ root: { flex: 1 } }}
                onChange={(ev, option) => {
                    setSelectedId(option?.id);
                }}
                options={entries.map(entry => ({
                    id: entry.id,
                    key: entry.id,
                    text: entry.title,
                    selected: entry.id === selectedId,
                    data: entry
                }))}
            />
            <Checkbox disabled={!canConfirm} checked={replaceExisting} onChange={(ev, checked) => setReplaceExisting(!!checked)} label="Replace?" />
            <IconButton disabled={!canConfirm} iconProps={{ iconName: "Accept" }} onClick={confirm} />
        </Stack>
    )
}
